"use client"

import { useState, useEffect } from "react"
import { observer } from "mobx-react-lite"

import {
  Activity,
  AlertTriangle,
  Users,
  Edit,
  Save,
  X,
  Trash2,
  User,
  Building2,
  Briefcase,
  Search,
  ChevronDown,
  ChevronUp,
} from "lucide-react"
import { useAppContext } from "../../../shared/functions/Context"
import type { IRateAssignment } from "../../../shared/models/three-sixty-feedback-models/RateAssignments"
import type { IUser } from "../../../shared/models/User"

interface AdminRatingAssignmentManagerProps {
  sessionId?: string
}

const AdminRatingAssignmentManager = observer(({ sessionId }: AdminRatingAssignmentManagerProps) => {
  const { api, store, ui } = useAppContext()
  const [initialLoading, setInitialLoading] = useState(!sessionId)
  const [savingLoading, setSavingLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [assignments, setAssignments] = useState<IRateAssignment[]>([])
  const [selectedSession, setSelectedSession] = useState<IRateAssignment | null>(null)
  const [editingAssignments, setEditingAssignments] = useState<Record<string, string[]>>({})
  const [searchTerm, setSearchTerm] = useState("")
  const [expandedRaters, setExpandedRaters] = useState<Set<string>>(new Set())
  const [dropdownSearchTerms, setDropdownSearchTerms] = useState<Record<string, string>>({})

  useEffect(() => {
    const loadData = async () => {
      setInitialLoading(true)
      try {
        await Promise.all([api.user.getAll(), api.ratingAssignments.getAll(), api.department.getAll()])
        const allAssignments = store.ratingAssignments.all.map((a) => a.asJson)

        if (sessionId) {
          const filtered = allAssignments.filter((a) => a.id === sessionId)
          setAssignments(filtered)
          if (filtered.length > 0) {
            handleSessionSelect(filtered[0])
          }
        } else {
          setAssignments(allAssignments)
        }
      } catch (err) {
        console.error("Failed to load data:", err)
        setError("Failed to load data.")
      } finally {
        setInitialLoading(false)
      }
    }
    loadData()
  }, [api, store.user, store.ratingAssignments, store.department, sessionId])

  const users = store.user.all.map((user) => user.asJson)
  const departments = store.department.all.map((dept) => dept.asJson)

  const getUserName = (userId: string): string => {
    const user = users.find((u) => u.uid === userId)
    return user ? `${user.displayName} ${user.lastName || ""}`.trim() : "Unknown User"
  }

  const getDepartmentName = (departmentId: string): string => {
    const department = departments.find((dept) => dept.id === departmentId)
    return department ? department.name : "Unknown Department"
  }

  const getUserDetails = (userId: string): IUser | null => {
    return users.find((u) => u.uid === userId) || null
  }

  const handleSessionSelect = (session: IRateAssignment) => {
    setSelectedSession(session)
    setEditingAssignments({ ...session.feedbackAssignments })
  }

  const handleAddRatee = (raterId: string, rateeId: string) => {
    if (!rateeId || raterId === rateeId) return

    setEditingAssignments((prev) => ({
      ...prev,
      [raterId]: prev[raterId] ? [...prev[raterId], rateeId] : [rateeId],
    }))
  }

  const handleRemoveRatee = (raterId: string, rateeId: string) => {
    setEditingAssignments((prev) => ({
      ...prev,
      [raterId]: prev[raterId]?.filter((id) => id !== rateeId) || [],
    }))
  }

  const handleSaveAssignments = async () => {
    if (!selectedSession) return

    setSavingLoading(true)
    try {
      const updatedSession: IRateAssignment = {
        ...selectedSession,
        feedbackAssignments: editingAssignments,
      }

      await api.ratingAssignments.update(updatedSession)

      // Update local state
      setAssignments((prev) => prev.map((session) => (session.id === selectedSession.id ? updatedSession : session)))
      setSelectedSession(updatedSession)

      ui.snackbar.load({
        id: Date.now(),
        type: "success",
        message: "Rating assignments updated successfully.",
        timeoutInMs: 5000,
      })
    } catch (error) {
      console.error("Error updating assignments:", error)
      ui.snackbar.load({
        id: Date.now(),
        type: "warning",
        message: "Failed to update assignments. Please try again.",
        timeoutInMs: 5000,
      })
    } finally {
      setSavingLoading(false)
    }
  }

  const toggleRaterExpansion = (raterId: string) => {
    setExpandedRaters((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(raterId)) {
        newSet.delete(raterId)
      } else {
        newSet.add(raterId)
      }
      return newSet
    })
  }

  const filteredUsers = users.filter(
    (user) =>
      user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const getAvailableRatees = (raterId: string) => {
    const currentRatees = editingAssignments[raterId] || []
    return users.filter((user) => user.uid !== raterId && !currentRatees.includes(user.uid))
  }

  const getFilteredAvailableRatees = (raterId: string) => {
    const availableRatees = getAvailableRatees(raterId)
    const dropdownSearch = dropdownSearchTerms[raterId] || ""

    if (!dropdownSearch.trim()) return availableRatees

    return availableRatees.filter(
      (user) =>
        user.displayName?.toLowerCase().includes(dropdownSearch.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(dropdownSearch.toLowerCase()) ||
        user.email?.toLowerCase().includes(dropdownSearch.toLowerCase()),
    )
  }

  if (initialLoading && !selectedSession) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200"></div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Activity className="h-5 w-5 text-blue-600 animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="text-center space-y-4">
          <div className="p-4 bg-red-100 rounded-full w-fit mx-auto">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Error Loading Data</h3>
            <p className="text-slate-600">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 relative">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Users className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Rating Assignment Manager</h2>
            <p className="text-slate-600">Manage who rates whom in 360° feedback sessions</p>
          </div>
        </div>
      </div>

      {!selectedSession && !sessionId ? (
        /* Session Selection */
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 p-6">
          <h3 className="text-xl font-bold text-slate-800 mb-4">Select a Session to Manage</h3>
          {assignments.length === 0 ? (
            <p className="text-slate-600">No rating sessions found.</p>
          ) : (
            <div className="space-y-4">
              {assignments.map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <div className="space-y-2">
                    <h4 className="font-semibold text-slate-800">{session.description}</h4>
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          session.status === "In Progress"
                            ? "bg-blue-100 text-blue-800"
                            : session.status === "Completed"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {session.status}
                      </span>
                      <span>{Object.keys(session.feedbackAssignments || {}).length} raters assigned</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleSessionSelect(session)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Manage Assignments
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Assignment Management */
        <div className="space-y-6">
          {/* Session Header */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-slate-800">{selectedSession?.description}</h3>
                <p className="text-slate-600">Manage rating assignments for this session</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveAssignments}
                  disabled={savingLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {savingLoading ? "Saving..." : "Save Changes"}
                </button>
                {!sessionId && (
                  <button
                    onClick={() => setSelectedSession(null)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    Back to Sessions
                  </button>
                )}
              </div>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
              />
            </div>
          </div>

          {/* Assignments List */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
            <div className="p-6 border-b border-slate-200">
              <h4 className="text-lg font-semibold text-slate-800">Current Assignments</h4>
              <p className="text-slate-600">Click on a rater to expand and modify their assignments</p>
            </div>

            <div className="divide-y divide-slate-200">
              {Object.entries(editingAssignments).map(([raterId, rateeIds]) => {
                const raterDetails = getUserDetails(raterId)
                const isExpanded = expandedRaters.has(raterId)

                if (!raterDetails) return null

                // Filter based on search
                if (
                  searchTerm &&
                  !raterDetails.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) &&
                  !raterDetails.lastName?.toLowerCase().includes(searchTerm.toLowerCase())
                ) {
                  return null
                }

                return (
                  <div key={raterId} className="p-6">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => toggleRaterExpansion(raterId)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                          <User className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-slate-800">
                            {raterDetails.displayName} {raterDetails.lastName}
                          </h5>
                          <div className="flex items-center gap-4 text-sm text-slate-600">
                            <div className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {getDepartmentName(raterDetails.department)}
                            </div>
                            <div className="flex items-center gap-1">
                              <Briefcase className="h-3 w-3" />
                              {raterDetails.jobTitle}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                          {rateeIds.length} ratees
                        </span>
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-6 space-y-4">
                        {/* Current Ratees */}
                        <div>
                          <h6 className="text-sm font-semibold text-slate-700 mb-3">Currently Rating:</h6>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {rateeIds.map((rateeId) => {
                              const rateeDetails = getUserDetails(rateeId)
                              if (!rateeDetails) return null

                              return (
                                <div
                                  key={rateeId}
                                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                                >
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                                      <User className="h-4 w-4 text-white" />
                                    </div>
                                    <span className="text-sm font-medium text-slate-700">
                                      {rateeDetails.displayName} {rateeDetails.lastName}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => handleRemoveRatee(raterId, rateeId)}
                                    className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              )
                            })}
                          </div>
                        </div>

                        {/* Add New Ratee */}
                        <div>
                          <h6 className="text-sm font-semibold text-slate-700 mb-3">Add New Ratee:</h6>
                          <div className="space-y-3">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                              <input
                                type="text"
                                placeholder="Search users to add..."
                                value={dropdownSearchTerms[raterId] || ""}
                                onChange={(e) =>
                                  setDropdownSearchTerms((prev) => ({
                                    ...prev,
                                    [raterId]: e.target.value,
                                  }))
                                }
                                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                            <div className="border border-slate-300 rounded-lg max-h-64 overflow-y-auto">
                              {getFilteredAvailableRatees(raterId).length > 0 ? (
                                <div className="divide-y divide-slate-200">
                                  {getFilteredAvailableRatees(raterId).map((user) => (
                                    <button
                                      key={user.uid}
                                      onClick={() => {
                                        handleAddRatee(raterId, user.uid)
                                        setDropdownSearchTerms((prev) => ({
                                          ...prev,
                                          [raterId]: "",
                                        }))
                                      }}
                                      className="w-full flex items-center gap-3 p-3 text-left hover:bg-blue-50 transition-colors"
                                    >
                                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                                        <User className="h-4 w-4 text-white" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-800">
                                          {user.displayName} {user.lastName}
                                        </p>
                                        <p className="text-xs text-slate-600">{getDepartmentName(user.department)}</p>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <div className="p-4 text-center text-slate-500 text-sm">
                                  {dropdownSearchTerms[raterId] ? "No users found" : "All users already assigned"}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {savingLoading && (
        <div className="absolute inset-0 bg-black/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200"></div>
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Activity className="h-5 w-5 text-blue-600 animate-pulse" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
})

export default AdminRatingAssignmentManager
